import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { SearchIcon, SlidersHorizontalIcon, XIcon, ArrowRightIcon } from 'lucide-react';
import { usePageMeta } from '../../hooks/usePageMeta';
import { useJobSearch } from '../../hooks/useJobSearch';
import { industries } from '../../data/industries';
import { jobs, SAMPLE_JOBS_NOTICE } from '../../data/jobs';
import { candidateResources } from '../../data/company';
import { postedLabel } from '../../utils/format';
import { PageHero } from '../../components/ui/PageHero';
import { SectionHeading } from '../../components/ui/SectionHeading';
import { Button } from '../../components/ui/Button';
import { JobCard } from '../../components/jobs/JobCard';
import { NewsletterForm } from '../../components/forms/NewsletterForm';
import type { JobFilters, JobSort } from '../../hooks/useJobSearch';
const seniorities = ['Board', 'C-Suite', 'Executive', 'Senior Management', 'Specialist'];
const types = ['Permanent', 'Interim', 'Contract', 'Fixed-term'];
const control = 'h-11 w-full rounded-md border border-line bg-white px-3 text-[15px] text-ink focus:border-midnight focus:outline-none focus:ring-2 focus:ring-gold/40';
export function Jobs() {
  usePageMeta('Find Jobs — Career Opportunities', 'Search executive, senior leadership and specialist professional opportunities with Corventa Talent Search.');
  const {
    filters,
    setFilter,
    clearAll,
    sort,
    setSort,
    results,
    shown,
    hasMore,
    loadMore,
    activeCount
  } = useJobSearch();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const recent = [...jobs].sort((a, b) => b.posted.localeCompare(a.posted)).slice(0, 4);
  const select = (key: keyof JobFilters, label: string, options: {
    value: string;
    label: string;
  }[]) => <div data-id="element-696" data-mp="1q7rv8ojzvpqf">
      <label htmlFor={`f-${key}`} className="mb-1.5 block text-sm font-medium text-ink" data-id="element-697" data-mp="1q5ynf5xnazgu">
        {label}
      </label>
      <select id={`f-${key}`} value={filters[key]} onChange={e => setFilter(key, e.target.value)} className={control} data-id="element-698" data-mp="1v4i9d6c8ob97">
        <option value="" data-id="element-699" data-mp="1n3akdbr6zwts">All</option>
        {options.map(o => <option key={o.value} value={o.value} data-id="element-700" data-mp="1j30iobafnskq">
            {o.label}
          </option>)}
      </select>
    </div>;
  const filterPanel = <div className="space-y-5" data-id="element-701" data-mp="1rw3ukf5fjxd9">
      <div data-id="element-702" data-mp="1rx52iar5g3qr">
        <label htmlFor="f-location" className="mb-1.5 block text-sm font-medium text-ink" data-id="element-703" data-mp="1rxeta0k0qexi">
          Location
        </label>
        <input id="f-location" value={filters.location} onChange={e => setFilter('location', e.target.value)} placeholder="City, country or remote" className={control} data-id="element-704" data-mp="148lw60rnu11b" />
      </div>
      {select('industry', 'Industry', industries.map(i => ({
      value: i.slug,
      label: i.name
    })))}
      {select('seniority', 'Seniority', seniorities.map(s => ({
      value: s,
      label: s
    })))}
      {select('type', 'Employment type', types.map(t => ({
      value: t,
      label: t
    })))}
      {activeCount > 0 && <button type="button" onClick={clearAll} className="text-sm font-medium text-royal hover:underline" data-id="element-705" data-mp="2kkj4in9pfphq">
          Clear all filters
        </button>}
    </div>;
  return <>
      <PageHero crumbs={[{
      label: 'Find Jobs'
    }]} kicker="Career opportunities" title="Explore Your Next Opportunity." intro="Executive, senior leadership and specialist professional roles. Search by title, keyword or skill, then refine by location, industry, seniority and type." data-id="element-706" data-mp="bdeaugxbddoe" />

      <section className="bg-ivory pb-24 pt-10" data-id="element-707" data-mp="q3d6abxhib06">
        <div className="container-page" data-id="element-708" data-mp="u1japdauoehx">
          <div className="rounded-md border border-gold/40 bg-gold-100 px-4 py-3 text-sm text-gold-700" data-id="element-709" data-mp="u4owaik9sy8w">{SAMPLE_JOBS_NOTICE}</div>

          <div className="mt-8 flex gap-3" data-id="element-710" data-mp="la0rsumirkl5">
            <div className="relative flex-1" data-id="element-711" data-mp="l8psb703wyzu">
              <label htmlFor="f-q" className="sr-only" data-id="element-712" data-mp="1904y59r3dyfu">
                Search by title, keyword or skill
              </label>
              <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-713" data-mp="1drmybvlajuzr" />
              <input id="f-q" type="search" value={filters.q} onChange={e => setFilter('q', e.target.value)} placeholder="Search by title, keyword or skill" className="h-14 w-full rounded-md border border-line bg-white pl-12 pr-4 text-base text-ink shadow-soft focus:border-midnight focus:outline-none focus:ring-2 focus:ring-gold/40" data-id="element-714" data-mp="3l820axvjknab" />
            </div>
            <button type="button" onClick={() => setFiltersOpen(o => !o)} aria-expanded={filtersOpen} aria-controls="mobile-filters" className="flex h-14 items-center gap-2 rounded-md border border-line bg-white px-4 text-sm font-medium text-ink lg:hidden" data-id="element-715" data-mp="3upyrenkgxhd">
              <SlidersHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-716" data-mp="rl2dp9h8buhg" />
              Filters{activeCount > 0 && ` (${activeCount})`}
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2" aria-label="Quick seniority filters" data-id="element-717" data-mp="u5i6h1f5jm24">
            {['', ...seniorities].map(s => <button key={s || 'all'} type="button" aria-pressed={filters.seniority === s} onClick={() => setFilter('seniority', s)} className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors duration-150 ${filters.seniority === s ? 'border-midnight bg-midnight text-white' : 'border-line bg-white text-ink hover:border-midnight'}`} data-id="element-718" data-mp="1423d1r6tl582">
                {s || 'All levels'}
              </button>)}
          </div>

          <AnimatePresence data-id="element-719" data-mp="1rtiihstw82nj">
            {filtersOpen && <motion.div id="mobile-filters" initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.22,
            ease: [0.23, 1, 0.32, 1]
          }} className="overflow-hidden lg:hidden" data-id="element-720" data-mp="1vu2e2rytiy7k">
                <div className="mt-4 rounded-lg border border-line bg-white p-5" data-id="element-721" data-mp="afe6sx4uisw0">{filterPanel}</div>
              </motion.div>}
          </AnimatePresence>

          <div className="mt-10 grid gap-10 lg:grid-cols-12" data-id="element-722" data-mp="5nmn96k3xnfd">
            <aside className="hidden lg:col-span-3 lg:block" aria-label="Filters" data-id="element-723" data-mp="kcso0yylflpd">
              <div className="sticky top-28 rounded-lg border border-line bg-white p-5" data-id="element-724" data-mp="ffacr1x3sue7">
                <h2 className="mb-5 font-display text-2xl font-semibold text-ink" data-id="element-725" data-mp="tea3cf46fjd0">Refine</h2>
                {filterPanel}
              </div>
            </aside>

            <div className="lg:col-span-9" data-id="element-726" data-mp="odcjzz7fkj9x">
              <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-727" data-mp="2v5dcezyf4pvg">
                <p className="text-sm text-muted" aria-live="polite" data-id="element-728" data-mp="2q721sn0aubc6">
                  {results.length} {results.length === 1 ? 'opportunity' : 'opportunities'} found
                </p>
                <div className="flex items-center gap-2" data-id="element-729" data-mp="1anuqemm655m5">
                  <label htmlFor="sort" className="text-sm text-muted" data-id="element-730" data-mp="1ancl70hj3h23">
                    Sort
                  </label>
                  <select id="sort" value={sort} onChange={e => setSort(e.target.value as JobSort)} className="h-9 rounded-md border border-line bg-white px-2 text-sm text-ink" data-id="element-731" data-mp="wud5axdu3ku0">
                    <option value="featured" data-id="element-732" data-mp="mwp17guc90z6">Featured first</option>
                    <option value="newest" data-id="element-733" data-mp="rxmqsehogxap">Most recent</option>
                  </select>
                </div>
              </div>

              {results.length === 0 ? <div className="mt-6 rounded-lg border border-dashed border-line bg-white px-6 py-16 text-center" data-id="element-734" data-mp="hz6wk2nct7wz">
                  <XIcon className="mx-auto h-8 w-8 text-muted" aria-hidden="true" data-id="element-735" data-mp="28b3806j1tuiv" />
                  <h3 className="mt-4 font-display text-2xl font-semibold text-ink" data-id="element-736" data-mp="2ccy6725onrsf">No opportunities match these filters</h3>
                  <p className="mx-auto mt-2 max-w-md text-muted" data-id="element-737" data-mp="1044fl2jvunof">Try broadening your search, or set up a job alert to hear about relevant roles.</p>
                  <div className="mt-6 flex justify-center gap-3" data-id="element-738" data-mp="v6uqxp4big1n">
                    <Button variant="navy" onClick={clearAll} data-id="element-739" data-mp="qcqif1felzyg">
                      Clear filters
                    </Button>
                    <Button variant="outline" to="#alerts" data-id="element-740" data-mp="lcvsp17d7gez">
                      Create a job alert
                    </Button>
                  </div>
                </div> : <>
                  <ul className="mt-6 grid gap-5 md:grid-cols-2" data-id="element-741" data-mp="7f787uh7mbag">
                    {shown.map(job => <li key={job.id} data-id="element-742" data-mp="2gcehxhpf2bk">
                        <JobCard job={job} data-id="element-743" data-mp="2he211fwpha6" />
                      </li>)}
                  </ul>
                  {hasMore && <div className="mt-10 text-center" data-id="element-744" data-mp="1wu3x3mi0gwj5">
                      <Button variant="outline" onClick={loadMore} data-id="element-745" data-mp="1wun62dpooal1">
                        Load more opportunities
                      </Button>
                    </div>}
                </>}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-24" data-id="element-746" data-mp="2e4hdnt9ybbsh">
        <div className="container-page grid gap-12 lg:grid-cols-12" data-id="element-747" data-mp="2e5sf3mfxc7gf">
          <div className="lg:col-span-5" data-id="element-748" data-mp="2j210viommywk">
            <SectionHeading kicker="Recently added" title="New this month." data-id="element-749" data-mp="1mc9x0sug3ze3" />
            <ul className="mt-8 divide-y divide-line border-y border-line" data-id="element-750" data-mp="1mecqjmut4ank">
              {recent.map(j => <li key={j.id} data-id="element-751" data-mp="1vcy7xeojljmr">
                  <Link to={`/jobs/${j.id}`} className="group flex items-center justify-between gap-4 py-4" data-id="element-752" data-mp="1vcoqifhkckso">
                    <span data-id="element-753" data-mp="20d2tvk87z6kd">
                      <span className="block font-medium text-ink group-hover:text-royal" data-id="element-754" data-mp="20dcjh6pcrbpj">{j.title}</span>
                      <span className="text-sm text-muted" data-id="element-755" data-mp="36u7p1uk77zkw">
                        {j.location} · {postedLabel(j.posted)}
                      </span>
                    </span>
                    <ArrowRightIcon className="h-4 w-4 text-muted group-hover:text-royal" aria-hidden="true" data-id="element-756" data-mp="2qqj0ahew3k3b" />
                  </Link>
                </li>)}
            </ul>
          </div>
          <div id="alerts" className="scroll-mt-28 rounded-xl bg-ivory p-8 md:p-10 lg:col-span-7" data-id="element-757" data-mp="30hdmxmw1slpa">
            <h2 className="font-display text-3xl font-semibold text-ink" data-id="element-758" data-mp="34hxm0w06lze2">Job alerts</h2>
            <p className="mb-6 mt-2 text-muted" data-id="element-759" data-mp="27threli7szu3">Be the first to hear about roles that match your level and expertise.</p>
            <NewsletterForm purpose="alerts" tone="light" data-id="element-760" data-mp="2crj8x1zzsr3g" />
            <div className="mt-8 border-t border-line pt-6" data-id="element-761" data-mp="2gsla3odgzd82">
              <p className="text-sm text-muted" data-id="element-762" data-mp="2lrxyidjzoscr">Prefer a confidential conversation instead?</p>
              <Button to="/candidates#register" variant="outline" size="sm" className="mt-3" arrow data-id="element-763" data-mp="2lplapjrjvnrs">
                Register as a candidate
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-ivory py-24" data-id="element-764" data-mp="b66vhb3v355e">
        <div className="container-page" data-id="element-765" data-mp="b55g4uftbwhv">
          <SectionHeading kicker="Career advice" title="Application guidance." data-id="element-766" data-mp="66u90rgwk03c" />
          <div className="mt-10 grid gap-10 md:grid-cols-2 lg:grid-cols-4" data-id="element-767" data-mp="68wxh72epl5y">
            {candidateResources.map(r => <div key={r.title} className="border-t border-line pt-6" data-id="element-768" data-mp="3tjwoal2v9hm5">
                <h3 className="font-display text-xl font-semibold text-ink" data-id="element-769" data-mp="3thtwpwm2fgfb">{r.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-muted" data-id="element-770" data-mp="3oigy6elu0kww">{r.text}</p>
              </div>)}
          </div>
        </div>
      </section>
    </>;
}